import { useEffect, useRef } from "react";
import { useMotionTuning, useTheme } from "../lib/tuning.js";

interface Piece {
  x: number;
  y: number;
  vx: number;
  vy: number;
  rot: number;
  vrot: number;
  w: number;
  h: number;
  colour: string;
  born: number;
}

/**
 * Paper-scrap confetti on a canvas.
 *
 * Deliberately rectangles rather than circles or emoji — the whole board is
 * card stock, so the celebration should look like torn paper, not a party app.
 */
export function Confetti({
  trigger,
  teamId,
}: {
  trigger: number;
  teamId: "rose" | "slate";
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const pieces = useRef<Piece[]>([]);
  const frame = useRef(0);
  const motion = useMotionTuning();
  const theme = useTheme();

  /*
   * The trigger this canvas has already celebrated.
   *
   * Firing on any run with a non-zero trigger meant a remount replayed the last
   * celebration — which is how a duplicate guess once threw confetti, the TV
   * being torn down and rebuilt on every rejected answer. That remount is gone,
   * but a burst should be caused by a new correct answer and nothing else.
   */
  const fired = useRef(trigger);

  useEffect(() => {
    if (trigger === 0 || trigger === fired.current) return;
    fired.current = trigger;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const { confettiCount, confettiSpread_deg, confettiVelocity } =
      motion.correct;
    const base = teamId === "rose" ? theme.palette.rose : theme.palette.slate;
    const palette = [
      base,
      theme.palette.affirm,
      theme.palette.caution,
      theme.palette.ink,
    ];
    const originX = canvas.width / 2;
    const originY = canvas.height * 0.52;
    const now = performance.now();

    for (let i = 0; i < confettiCount; i++) {
      const spread = (confettiSpread_deg * Math.PI) / 180;
      const angle = -Math.PI / 2 + (Math.random() - 0.5) * spread;
      const speed = confettiVelocity * (0.55 + Math.random() * 0.75);
      pieces.current.push({
        x: originX + (Math.random() - 0.5) * 220,
        y: originY,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        rot: Math.random() * Math.PI,
        vrot: (Math.random() - 0.5) * 0.3,
        w: 6 + Math.random() * 9,
        h: 3 + Math.random() * 6,
        colour: palette[Math.floor(Math.random() * palette.length)]!,
        born: now,
      });
    }
  }, [trigger, teamId, motion.correct, theme.palette]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = canvas.offsetWidth * dpr;
      canvas.height = canvas.offsetHeight * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const { confettiGravity, confettiDrag, confettiLife_ms } = motion.correct;
    const loop = () => {
      const now = performance.now();
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      pieces.current = pieces.current.filter(
        (p) => now - p.born < confettiLife_ms,
      );

      for (const p of pieces.current) {
        p.vy += confettiGravity;
        p.vx *= confettiDrag;
        p.vy *= confettiDrag;
        p.x += p.vx;
        p.y += p.vy;
        p.rot += p.vrot;
        const life = (now - p.born) / confettiLife_ms;
        ctx.save();
        ctx.globalAlpha = Math.max(0, 1 - life * life);
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillStyle = p.colour;
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx.restore();
      }
      frame.current = requestAnimationFrame(loop);
    };
    frame.current = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(frame.current);
      window.removeEventListener("resize", resize);
    };
  }, [motion.correct]);

  return <canvas ref={canvasRef} className="confetti" aria-hidden="true" />;
}
